import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { usePermission, useOnClickOutside, useRouter } from 'hooks';
import Loading from 'components/loading/Loading';
import { UiIcon, UiTextField, UiLabel } from 'ui-components';
import { useDispatch, useSelector } from 'react-redux';
import { fetchTags } from 'store';
import { getCurrentSlice } from 'utils';
import { useTranslation } from 'react-i18next';
import { IncludeOrExcludeRadios } from '../resources-filter/components';
import { StyledResourceCategoryFilter } from './ResourceCategoryFilter.styles';

const platformModel = {
  multiPlatform: undefined,
  news: 'newsagencies',
  newspaper: 'newspaperagencies',
  kiosk: 'newspaperagencies',
  instagram: 'instagramusers',
  instagramComment: 'instagramusers',
  twitter: 'twitterusers',
  telegram: 'telegramchannels',
  nativePlatforms: 'iranian-platforms-channels',
  rubika: 'iranian-platforms-channels',
  eitaa: 'iranian-platforms-channels',
  bale: 'iranian-platforms-channels',
  virasty: 'virastyusers',
};

function ResourceCategoryFilterDropDown({ tags, renderFilteredTags }) {
  const { t } = useTranslation();

  return (
    <ul data-testid='filter-resource-category-list'>
      {tags.loading && (
        <li className='loading-section'>
          <Loading spinning={true} />
        </li>
      )}
      {renderFilteredTags()}
      {tags.data?.length === 0 && (
        <li className='flex center muted'>
          <span>{t('filter:noResourceRegisteredForThisPlatform')}</span>
        </li>
      )}
    </ul>
  );
}

ResourceCategoryFilterDropDown.propTypes = {
  tags: PropTypes.object,
  renderFilteredTags: PropTypes.element,
};

const resourceCategoryFilterDefault = {};

export const ResourceCategoryFilter = (props) => {
  const { componentName, parentPlatform, className } = props;
  const tags = useSelector((state) => state.tagReducer.tags);

  const resourceCategoryFilter =
    useSelector((state) => {
      const filterState = parentPlatform
        ? state[state.filterReducer.filtersContext]?.filters?.[
            parentPlatform
          ]?.[componentName]
        : state[state.filterReducer.filtersContext]?.filters?.[componentName];
      return filterState;
    }) || resourceCategoryFilterDefault;

  const reducerName = useSelector(
    (state) => state.filterReducer.filtersContext
  );

  const {
    filterType,
    selectedValue,
    platform,
    warning_label,
    hasIncludeExclude,
  } = resourceCategoryFilter || {};

  const { setSelectedFilterValue, resetFilter } = getCurrentSlice(reducerName);
  const dispatch = useDispatch();
  const { clearRouterFilters } = useRouter();
  const [filteredText, setFilteredText] = useState('');
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const { userPermission } = usePermission();
  const resourceCategoryFilterRef = useRef(null);
  const { t } = useTranslation();

  useOnClickOutside(resourceCategoryFilterRef, () =>
    setIsDropdownVisible(false)
  );

  const { permission: hasTagSearchPermission } = useMemo(
    () => userPermission('TaggedContent', 'update'),
    [userPermission]
  );

  /**
   * دریافت لیست دسته بندی تگ های منبع
   */
  useEffect(() => {
    if (hasTagSearchPermission && tags.data.length <= 0) {
      dispatch(
        fetchTags({ platformForApi: platformModel[parentPlatform || platform] })
      );
    }
  }, []);

  /**
   * انتخاب تگ منبع و ذخیره آن در فیلترها
   */
  const handleSelectResourceTag = useCallback(
    (id) => {
      dispatch(
        setSelectedFilterValue({
          value: id,
          componentName,
          parentPlatform,
        })
      );
      setIsDropdownVisible(false);
      setFilteredText('');
      clearRouterFilters();
    },
    [
      clearRouterFilters,
      componentName,
      dispatch,
      parentPlatform,
      setSelectedFilterValue,
    ]
  );

  const handleSearchTypeChange = (event) => {
    dispatch(
      setSelectedFilterValue({
        filterType: event.target.value,
        componentName,
        parentPlatform,
      })
    );
  };

  /**
   * حذف تگ منبع از لیست انتخاب شده ها
   * @param {*} index
   */
  const handleRemoveResourceTag = () => {
    dispatch(
      resetFilter({
        componentName,
        parentPlatform,
      })
    );
    clearRouterFilters();
  };

  const renderFilteredTags = useCallback(() => {
    const filteredTags =
      platform === 'multiPlatform'
        ? tags.data
        : tags.data.filter(
            (tag) =>
              tag.platforms[platformModel[parentPlatform || platform]] > 0
          );
    return filteredTags.map(
      ({ id, name }) =>
        name.includes(filteredText) && (
          <li
            key={id}
            onClick={() => handleSelectResourceTag(id)}
            className={classNames({ selected: id === selectedValue })}
          >
            <label>{name}</label>
          </li>
        )
    );
  }, [
    filteredText,
    handleSelectResourceTag,
    parentPlatform,
    platform,
    selectedValue,
    tags.data,
  ]);

  if (!hasTagSearchPermission) return null;

  function ResourceCategoryFilterSelectedItem() {
    return tags.data.map(
      (tag) =>
        tag.id === selectedValue && (
          <div className='selected-resource-tags flex' key={tag.id}>
            <span className='resource-tag flex'>
              {tag?.name}
              <UiIcon icon='ri-close-line' onClick={handleRemoveResourceTag} />
            </span>
          </div>
        )
    );
  }

  return (
    <StyledResourceCategoryFilter>
      {warning_label && (
        <UiLabel size={10} color='#F16063'>
          {t(warning_label)}
        </UiLabel>
      )}
      <div
        className={classNames('filter-resource-tag', className)}
        ref={resourceCategoryFilterRef}
      >
        {hasIncludeExclude && (
          <IncludeOrExcludeRadios
            onSearchTypeChange={handleSearchTypeChange}
            selectedValue={filterType}
            disabled={resourceCategoryFilter?.isDisabled}
            includeLabel={t('filter:onlySelectedResoucesCategory')}
            excludeLabel={t('filter:excludeSelectedResourcesCategory')}
          />
        )}
        <UiTextField
          value={filteredText}
          onChange={(e) => {
            setFilteredText(e.target.value);
          }}
          onClick={() =>
            !resourceCategoryFilter?.isDisabled && setIsDropdownVisible(true)
          }
          className='category-name-input'
          placeholder={t('filter:searchPlaceholder')}
          InputProps={{
            startAdornment: (
              <UiIcon icon='ri-search-line' className='search-icon' />
            ),
            'data-testid': 'filter-resource-category-name-input',
          }}
          disabled={resourceCategoryFilter?.isDisabled}
        />
        {isDropdownVisible && (
          <ResourceCategoryFilterDropDown
            tags={tags}
            renderFilteredTags={renderFilteredTags}
          />
        )}
        {selectedValue && <ResourceCategoryFilterSelectedItem />}
      </div>
    </StyledResourceCategoryFilter>
  );
};

ResourceCategoryFilter.propTypes = {
  componentName: PropTypes.string.isRequired,
  parentPlatform: PropTypes.string,
  className: PropTypes.string,
};
